import "@/App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import { SystemConfigProvider } from "./lib/systemConfig";
import { InboxWatcherProvider } from "./lib/inboxWatcher";
import { Toaster } from "sonner";
import { ConfirmDialogHost } from "./lib/confirmDialog";
import PdfPreviewHost from "./components/PdfPreviewHost";
import CookieConsent from "./components/CookieConsent";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { useEffect } from "react";

import Header from "./components/Header";
import Footer from "./components/Footer";
import MobileBottomNav from "./components/MobileBottomNav";
import MobileNotifyPrompt from "./components/MobileNotifyPrompt";
import useUserNotifications from "./lib/useUserNotifications";

import HomePage from "./pages/HomePage";
import VitoVespaPage from "./pages/VitoVespaPage";
import VitoVespaFlyerPage from "./pages/VitoVespaFlyerPage";
import PublicTeaserPage from "./pages/PublicTeaserPage";
import SearchPage from "./pages/SearchPage";
import DiscoverPage from "./pages/DiscoverPage";
import ListingDetailPage from "./pages/ListingDetailPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import WelcomePage from "./pages/WelcomePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import AdminTicketsPage from "./pages/AdminTicketsPage";
import AdminAssistantStatsPage from "./pages/AdminAssistantStatsPage";
import IntranetPage from "./pages/IntranetPage";
import ChatbotWidget from "./components/assistant/ChatbotWidget";
import InvoicesPage from "./pages/InvoicesPage";
import WMSPage from "./pages/WMSPage";
import WinterlagerPage from "./pages/wms/WinterlagerPage";
import UsageCostsPage from "./pages/UsageCostsPage";
import EmailClientPage from "./pages/EmailClientPage";
import TeamChatPage from "./pages/TeamChatPage";
import AdminOperatorPage from "./pages/AdminOperatorPage";
import PayrollPage from "./pages/PayrollPage";
import LeasingReturnPage from "./pages/leasing/LeasingReturnPage";
import FinancingExpiriesPage from "./pages/FinancingExpiriesPage";
import RoteNummerFahrtenbuchPage from "./pages/RoteNummerFahrtenbuchPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ScanCorrectionsPage from "./pages/ScanCorrectionsPage";
import VehicleInventoryPage from "./pages/VehicleInventoryPage";
import WorkshopOrdersPage from "./pages/WorkshopOrdersPage";
import MobileOrderCapturePage from "./pages/MobileOrderCapturePage";
import DispositionPage from "./pages/DispositionPage";
import VersandCockpitPage from "./pages/VersandCockpitPage";
import SocialMediaStudioPage from "./pages/SocialMediaStudioPage";
import AdminMarketWatchPage from "./pages/AdminMarketWatchPage";
import DealerOrdersPage from "./pages/DealerOrdersPage";
import OrderTrackingPublicPage from "./pages/OrderTrackingPublicPage";
import B2BDealsPage from "./pages/B2BDealsPage";
import TransportPlanningPage from "./pages/TransportPlanningPage";
import BuchhaltungPage from "./pages/BuchhaltungPage";
import GelangensnachweisPage from "./pages/GelangensnachweisPage";
import GelangensConfirmPage from "./pages/GelangensConfirmPage";
import VideoBeratungPage from "./pages/VideoBeratungPage";
import DealerCompetitorsPage from "./pages/DealerCompetitorsPage";
import PublicVideoMeetingPage from "./pages/PublicVideoMeetingPage";
import WorkshopCalendarPage from "./pages/WorkshopCalendarPage";
import SharedWorkOrdersPage from "./pages/SharedWorkOrdersPage";
import BankImportPage from "./pages/BankImportPage";
import DeliveryNoteScanPage from "./pages/DeliveryNoteScanPage";
import DocumentOutboxPage from "./pages/DocumentOutboxPage";
import DocumentCenterPage from "./pages/DocumentCenterPage";
import AiEnrichPage from "./pages/AiEnrichPage";
import PasskeySettingsPage from "./pages/PasskeySettingsPage";
import EmployeesPage from "./pages/EmployeesPage";
import VertretungsplanPage from "./pages/VertretungsplanPage";
import EmployeeUtilizationPage from "./pages/EmployeeUtilizationPage";
import DealerReviewsPage from "./pages/DealerReviewsPage";
import AppointmentPortalPage from "./pages/AppointmentPortalPage";
import ZulassungStatusPage from "./pages/ZulassungStatusPage";
import BelegePortalPage from "./pages/BelegePortalPage";
import EmployeeInvitePage from "./pages/EmployeeInvitePage";
import ArticleScanPage from "./pages/ArticleScanPage";
import ArticleCheckPage from "./pages/ArticleCheckPage";
import ArticleInventoryScanPage from "./pages/ArticleInventoryScanPage";
import GoodsReceiptScanPage from "./pages/GoodsReceiptScanPage";
import MobileSignPage from "./pages/MobileSignPage";
import ProbefahrtSignPage from "./pages/ProbefahrtSignPage";
import LeasingPortalPage from "./pages/LeasingPortalPage";
import LeasingPhotoGuidePage from "./pages/LeasingPhotoGuidePage";
import MobileScanPage from "./pages/MobileScanPage";
import MobileOutboxUploadPage from "./pages/MobileOutboxUploadPage";
import PublicConsentPage from "./pages/PublicConsentPage";
import AbmeldenPage from "./pages/AbmeldenPage";
import InvoiceReminderPortalPage from "./pages/InvoiceReminderPortalPage";
import AdminCronStatusPage from "./pages/AdminCronStatusPage";
import AdminHealthMonitorPage from "./pages/AdminHealthMonitorPage";
import AdminHsnTsnPage from "./pages/AdminHsnTsnPage";
import AdminSystemMaintenancePage from "./pages/AdminSystemMaintenancePage";
import AdminVehicleDuplicatesPage from "./pages/AdminVehicleDuplicatesPage";
import AdminCustomerDuplicatesPage from "./pages/AdminCustomerDuplicatesPage";
import AdminEmailHealthPage from "./pages/AdminEmailHealthPage";
import VermittlerCommissionsPage from "./pages/VermittlerCommissionsPage";
import QuoteAcceptPage from "./pages/QuoteAcceptPage";
import ValuationToolPage from "./pages/ValuationToolPage";
import AdminDatevAutoSendPage from "./pages/AdminDatevAutoSendPage";
import PurchaseOrdersPage from "./pages/PurchaseOrdersPage";
import ShopPage from "./pages/ShopPage";
import ShopArticlePage from "./pages/ShopArticlePage";
import ShopCheckoutSuccessPage from "./pages/ShopCheckoutSuccessPage";
import ShopCheckoutCancelledPage from "./pages/ShopCheckoutCancelledPage";
import MyOrdersPage from "./pages/MyOrdersPage";
import CreateListingPage from "./pages/CreateListingPage";
import CallerLookupPage from "./pages/CallerLookupPage";
import PartnerCompaniesPage from "./pages/PartnerCompaniesPage";
import DealerVehicleImagesPage from "./pages/DealerVehicleImagesPage";
import PaperInventoryPage from "./pages/PaperInventoryPage";
import DealerLeadsPage from "./pages/DealerLeadsPage";
import CompanyCulturePage from "./pages/CompanyCulturePage";
import EnrichmentReviewPage from "./pages/EnrichmentReviewPage";
import SystemEmailsPage from "./pages/SystemEmailsPage";
import EmailOutboxPage from "./pages/EmailOutboxPage";
import PartnerConfirmPage from "./pages/PartnerConfirmPage";
import DealerProfilePage from "./pages/DealerProfilePage";
import DealersPage from "./pages/DealersPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import AdminPawnPage from "./pages/AdminPawnPage";
import ServiceProviderRegisterPage from "./pages/ServiceProviderRegisterPage";
import ServiceProviderDashboard from "./pages/ServiceProviderDashboard";
import AdminServiceProvidersPage from "./pages/AdminServiceProvidersPage";
import AdminWalletPage from "./pages/AdminWalletPage";
import AdminLeadsPage from "./pages/AdminLeadsPage";
import AdminSystemCategoriesPage from "./pages/AdminSystemCategoriesPage";
import OffeneZulassungenPage from "./pages/OffeneZulassungenPage";
import AdminContactLeadsPage from "./pages/AdminContactLeadsPage";
import AuctionsPage from "./pages/AuctionsPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AdminListingLogPage from "./pages/AdminListingLogPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import HelpOverviewPage from "./pages/HelpOverviewPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import AgbPage from "./pages/AgbPage";
import DatenschutzPage from "./pages/DatenschutzPage";
import ImpressumPage from "./pages/ImpressumPage";
import MeineDatenPage from "./pages/MeineDatenPage";
import AvvPage from "./pages/AvvPage";
import DealerTermsPage from "./pages/DealerTermsPage";
import WiderrufPage from "./pages/WiderrufPage";
import BarrierefreiheitPage from "./pages/BarrierefreiheitPage";
import CompliancePage from "./pages/CompliancePage";
import AdminTopListingPage from "./pages/AdminTopListingPage";
import DealerWerbemittelPage from "./pages/DealerWerbemittelPage";
import ListingQrCardPage from "./pages/ListingQrCardPage";
import AdminDealersPage from "./pages/AdminDealersPage";
import AdminImpersonationPage from "./pages/AdminImpersonationPage";
import AdminDealerAuditPage from "./pages/AdminDealerAuditPage";
import AdminCompliancePage from "./pages/AdminCompliancePage";
import AdminDokuPage from "./pages/AdminDokuPage";
import AdminGoBDPage from "./pages/AdminGoBDPage";
import AdminComplianceHistoryPage from "./pages/AdminComplianceHistoryPage";
import AdminSpamFlagsPage from "./pages/AdminSpamFlagsPage";
import KiFeaturesPage from "./pages/KiFeaturesPage";
import WasKannTwoWheelsPage from "./pages/WasKannTwoWheelsPage";
import VermittlerDashboardPage from "./pages/VermittlerDashboardPage";
import AdminVermittlersPage from "./pages/AdminVermittlersPage";
import AdminAccountToolsPage from "./pages/AdminAccountToolsPage";
import AdminNewsletterPage from "./pages/AdminNewsletterPage";
import AdminVermittlerReferralsPage from "./pages/AdminVermittlerReferralsPage";
import AdminSmsHistoryPage from "./pages/AdminSmsHistoryPage";
import AdminPortalConsentLogPage from "./pages/AdminPortalConsentLogPage";
import AdminTariffsPage from "./pages/AdminTariffsPage";
import AdminUsageBillingPage from "./pages/AdminUsageBillingPage";
import AdminPstgReportPage from "./pages/AdminPstgReportPage";
import AdminListingReportsPage from "./pages/AdminListingReportsPage";
import AdminReviewsPage from "./pages/AdminReviewsPage";
import AdminPointsPage from "./pages/AdminPointsPage";
import TeamShowcasePage from "./pages/TeamShowcasePage";
import ReferralLandingPage from "./pages/ReferralLandingPage";
import DealerBuyRequestDetailPage from "./pages/DealerBuyRequestDetailPage";
import BuyRequestTermsPage from "./pages/BuyRequestTermsPage";
import BuyRequestPage from "./pages/BuyRequestPage";
import BuyRequestStatusPage from "./pages/BuyRequestStatusPage";
import ComparePage from "./pages/ComparePage";
import CompareDrawer from "./components/CompareDrawer";
import WorkshopBookingPage from "./pages/WorkshopBookingPage";
import CommandPalette, { CommandPaletteFAB } from "./components/CommandPalette";
import GlobalActionBanner from "./components/GlobalActionBanner";
import OnboardingTour from "./components/OnboardingTour";
import TaskTitleBadge from "./components/TaskTitleBadge";
import TaskPushPrompt from "./components/TaskPushPrompt";
import AppErrorBoundary from "./components/AppErrorBoundary";
import { useAuth } from "./lib/auth";

function App() {
    return (
        <AppErrorBoundary>
            <AuthProvider>
                <SystemConfigProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <InboxWatcherProvider>
                            <AppShell />
                        </InboxWatcherProvider>
                    </BrowserRouter>
                </SystemConfigProvider>
            </AuthProvider>
        </AppErrorBoundary>
    );
}

// Globaler Scroll-to-Top bei Routenwechsel (User-Wunsch): Beim Navigieren auf
// eine neue Seite immer nach oben scrollen, statt die alte Scroll-Position zu
// behalten. Lauscht auf pathname-Änderungen des React Routers.
function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [pathname]);
    return null;
}

function AppShell() {
    useUserNotifications();
    const { user, loading } = useAuth();
    const { pathname } = useLocation();

    // Geschlossene Plattform (User-Wunsch Jun-2026): Nicht eingeloggte Besucher
    // sehen ausschließlich die öffentliche Teaser-Seite. Erreichbar ohne Login
    // bleiben nur Auth-Seiten, Pflicht-Rechtsseiten (Impressum/Datenschutz) und
    // token-basierte Kundenportale (per geheimem Link, nicht auffindbar).
    const PUBLIC_AUTH = new Set(["/login", "/forgot-password", "/reset-password", "/verify-email", "/auth/callback"]);
    const PUBLIC_REGISTER_PREFIX = "/register";  // Fix Jul-2026: Registrierung (auch /register/service-provider) trotz geschlossener Plattform erreichbar
    const PUBLIC_LEGAL = new Set(["/impressum", "/datenschutz", "/agb", "/widerruf", "/barrierefreiheit",
        "/vito-vespa", "/vito-vespa/flyer",
        "/legal/agb", "/legal/datenschutz", "/legal/impressum", "/legal/avv",
        "/legal/haendler-nutzungsbedingungen", "/legal/buy-request-terms", "/legal/compliance"]);
    const PUBLIC_TOKEN_PREFIXES = ["/leasing/", "/appointment/", "/belege/", "/einwilligung/", "/abmelden/", "/zahlung/",
        "/quote-accept/", "/partner-bestaetigung/", "/m/scan/", "/m/outbox/", "/zulassung-status/", "/v/", "/werkstatt/", "/shop/", "/shop-checkout/",
        "/gn/confirm/", "/mitarbeiter-einladung/", "/artikel-scan/", "/artikel-check/", "/inventur-scan/", "/wareneingang-scan/", "/vertrag-signieren/", "/probefahrt-signieren/", "/bestellung/", "/beratung/", "/fotos/"];
    const isPublicAllowed = PUBLIC_AUTH.has(pathname) || PUBLIC_LEGAL.has(pathname)
        || pathname === PUBLIC_REGISTER_PREFIX || pathname.startsWith(PUBLIC_REGISTER_PREFIX + "/")
        || PUBLIC_TOKEN_PREFIXES.some((p) => pathname.startsWith(p));

    if (!user && !loading && !isPublicAllowed) {
        // Jul-2026 (User-Wunsch): Deep-Links (z. B. aus Nachfass-Mails) überleben das
        // Teaser-Gate — Partner-Login springt nach Anmeldung zurück zum Ziel.
        const nextTarget = pathname !== "/" ? pathname + window.location.search : "";
        return (
            <div className="App min-h-screen">
                <PublicTeaserPage next={nextTarget} />
                <ChatbotWidget />
                <Toaster position="top-center" richColors />
            </div>
        );
    }

    const showChrome = !!user;
    // Aug-2026 (Bugfix): Video-Beratung ist eine Vollbild-Seite — auch für
    // eingeloggte Berater OHNE Header/Task-Banner rendern (überlappte sonst).
    const bareLayout = pathname.startsWith("/leasing/") || pathname.startsWith("/beratung/") || pathname.startsWith("/fotos/");
    if (bareLayout) {
        return (
            <div className="App min-h-screen flex flex-col">
                <a href="#main-content" className="skip-link" data-testid="skip-to-content">Zum Hauptinhalt springen</a>
                <main id="main-content" className="flex-1">
                    <Routes>
                        <Route path="/leasing/:token" element={<LeasingPortalPage />} />
                        <Route path="/fotos/:token" element={<LeasingPhotoGuidePage />} />
                        <Route path="/beratung/:token" element={<PublicVideoMeetingPage />} />
                    </Routes>
                </main>
            </div>
        );
    }
    return (
        <div className="App min-h-screen flex flex-col pb-16 md:pb-0">
            <a href="#main-content" className="skip-link" data-testid="skip-to-content">Zum Hauptinhalt springen</a>
            <ImpersonationBanner />
            {showChrome && (
                <div className="sticky top-0 z-50">
                    <Header />
                    {user && <GlobalActionBanner />}
                </div>
            )}
            <main id="main-content" className="flex-1">
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/vito-vespa" element={<VitoVespaPage />} />
                    <Route path="/vito-vespa/flyer" element={<VitoVespaFlyerPage />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/discover" element={<DiscoverPage />} />
                    <Route path="/listing/:id" element={<ListingDetailPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/willkommen" element={<WelcomePage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/passkeys" element={<PasskeySettingsPage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/dashboard/settings" element={<Navigate to="/dashboard?tab=settings" replace />} />
                    <Route path="/dealer/invoices" element={<InvoicesPage />} />
                    <Route path="/dealer/wms" element={<WMSPage />} />
                    <Route path="/dealer/winterlager" element={<WinterlagerPage />} />
                    <Route path="/dealer/kosten" element={<UsageCostsPage />} />
                    <Route path="/dealer/email" element={<EmailClientPage />} />
                    <Route path="/dealer/team-chat" element={<TeamChatPage />} />
                    <Route path="/dealer/lohn" element={<PayrollPage />} />
                    <Route path="/dealer/leasingrueckgabe" element={<LeasingReturnPage />} />
                    <Route path="/dealer/financing-expiries" element={<FinancingExpiriesPage />} />
                    <Route path="/dealer/rote-nummer" element={<RoteNummerFahrtenbuchPage />} />
                    <Route path="/dealer/analytics" element={<AnalyticsPage />} />
                    <Route path="/dealer/fahrzeuginventur" element={<VehicleInventoryPage />} />
                    <Route path="/intranet" element={<IntranetPage />} />
                    <Route path="/dealer/workshop-orders" element={<WorkshopOrdersPage />} />
                    {/* Jul-2026 (User-Meldung): Alt-Link /dealer/workshop → endloser Spinner (fiel in /dealer/:id) */}
                    <Route path="/dealer/workshop" element={<Navigate to="/dealer/workshop-orders" replace />} />
                    {/* Jul-2026 (Bugfix, User-Meldung): KI-Assistent-Navigationsziele, die es
                        als Route nie gab — leiteten auf eine leere Seite („lädt dauerhaft"). */}
                    <Route path="/dealer/dashboard" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dealer/finanzierungen" element={<Navigate to="/dealer/financing-expiries" replace />} />
                    <Route path="/dealer/customers" element={<Navigate to="/dealer/invoices?tab=customers" replace />} />
                    <Route path="/werkstatt/erfassen/:wid" element={<MobileOrderCapturePage />} />
                    <Route path="/dealer/disposition" element={<DispositionPage />} />
                    <Route path="/dealer/versand" element={<VersandCockpitPage />} />
                    <Route path="/dealer/social" element={<SocialMediaStudioPage />} />
                    <Route path="/dealer/offene-zulassungen" element={<OffeneZulassungenPage />} />
                    <Route path="/dealer/order-tracking" element={<DealerOrdersPage />} />
                    <Route path="/dealer/b2b-anfragen" element={<B2BDealsPage />} />
                    <Route path="/dealer/transport" element={<TransportPlanningPage />} />
                    <Route path="/dealer/buchhaltung" element={<BuchhaltungPage />} />
                    <Route path="/dealer/gelangensnachweis" element={<GelangensnachweisPage />} />
                    <Route path="/dealer/videoberatung" element={<VideoBeratungPage />} />
                    <Route path="/dealer/scan-korrekturen" element={<ScanCorrectionsPage />} />
                    <Route path="/dealer/mitbewerber" element={<DealerCompetitorsPage />} />
                    <Route path="/dealer/anrufe" element={<CallerLookupPage />} />
                    <Route path="/dealer/partner" element={<PartnerCompaniesPage />} />
                    <Route path="/dealer/partners" element={<Navigate to="/dealer/partner" replace />} />
                    <Route path="/dealer/bilddatenbank" element={<DealerVehicleImagesPage />} />
                    <Route path="/dealer/papier-inventur" element={<PaperInventoryPage />} />
                    <Route path="/dealer/leads" element={<DealerLeadsPage />} />
                    <Route path="/dealer/firmen-dna" element={<CompanyCulturePage />} />
                    <Route path="/dealer/wms/anreicherung" element={<EnrichmentReviewPage />} />
                    <Route path="/dealer/system-emails" element={<SystemEmailsPage />} />
                    <Route path="/dealer/email-freigabe" element={<EmailOutboxPage />} />
                    <Route path="/dealer/workshop-orders/shared-with-me" element={<SharedWorkOrdersPage />} />
                    <Route path="/dealer/workshop-calendar" element={<WorkshopCalendarPage />} />
                    <Route path="/dealer/bank-import" element={<BankImportPage />} />
                    <Route path="/dealer/wms/scan-delivery" element={<DeliveryNoteScanPage />} />
                    <Route path="/dealer/documents" element={<DocumentCenterPage />} />
                    <Route path="/dealer/documents/outbox" element={<Navigate to="/dealer/documents" replace />} />
                    <Route path="/dealer/documents/inbox" element={<Navigate to="/dealer/documents" replace />} />
                    <Route path="/dealer/ai-enrich" element={<AiEnrichPage />} />
                    <Route path="/dealer/employees" element={<EmployeesPage />} />
                    <Route path="/dealer/vertretungsplan" element={<VertretungsplanPage />} />
                    <Route path="/dealer/employee-utilization" element={<EmployeeUtilizationPage />} />
                    <Route path="/dealer/workshop-reviews" element={<DealerReviewsPage />} />
                    <Route path="/appointment/:token" element={<AppointmentPortalPage />} />
                    <Route path="/zulassung-status/:token" element={<ZulassungStatusPage />} />
                    <Route path="/belege/:token" element={<BelegePortalPage />} />
                    <Route path="/bestellung/:token" element={<OrderTrackingPublicPage />} />
                    <Route path="/mitarbeiter-einladung/:token" element={<EmployeeInvitePage />} />
                    <Route path="/artikel-scan/:token" element={<ArticleScanPage />} />
                    <Route path="/artikel-check/:token" element={<ArticleCheckPage />} />
                    <Route path="/inventur-scan/:token" element={<ArticleInventoryScanPage />} />
                    <Route path="/wareneingang-scan/:token" element={<GoodsReceiptScanPage />} />
                    <Route path="/vertrag-signieren/:token" element={<MobileSignPage />} />
                    <Route path="/probefahrt-signieren/:token" element={<ProbefahrtSignPage />} />
                    <Route path="/leasing/:token" element={<LeasingPortalPage />} />
                    <Route path="/gn/confirm/:token" element={<GelangensConfirmPage />} />
                    <Route path="/beratung/:token" element={<PublicVideoMeetingPage />} />
                    <Route path="/m/scan/:token" element={<MobileScanPage />} />
                    <Route path="/m/outbox/:token" element={<MobileOutboxUploadPage />} />
                    <Route path="/einwilligung/:token" element={<PublicConsentPage />} />
                    <Route path="/abmelden/:token" element={<AbmeldenPage />} />
                    <Route path="/zahlung/:token" element={<InvoiceReminderPortalPage />} />
                    <Route path="/admin/cron-status" element={<AdminCronStatusPage />} />
                    <Route path="/admin/health-monitor" element={<AdminHealthMonitorPage />} />
                    <Route path="/admin/hsn-tsn" element={<AdminHsnTsnPage />} />
                    <Route path="/admin/systempflege" element={<AdminSystemMaintenancePage />} />
                    <Route path="/admin/market-watch" element={<AdminMarketWatchPage />} />
                    <Route path="/admin/vehicle-duplicates" element={<AdminVehicleDuplicatesPage />} />
                    <Route path="/admin/customer-duplicates" element={<AdminCustomerDuplicatesPage />} />
                    <Route path="/admin/betreiber" element={<AdminOperatorPage />} />
                    <Route path="/admin/email-health" element={<AdminEmailHealthPage />} />
                    <Route path="/vermittler/commissions" element={<VermittlerCommissionsPage />} />
                    <Route path="/quote-accept/:token" element={<QuoteAcceptPage />} />
                    <Route path="/partner-bestaetigung/:token" element={<PartnerConfirmPage />} />
                    <Route path="/dealer/valuation" element={<ValuationToolPage />} />
                    <Route path="/admin/datev/auto-send" element={<AdminDatevAutoSendPage />} />
                    <Route path="/dealer/purchase-orders" element={<PurchaseOrdersPage />} />
                    <Route path="/create-listing" element={<CreateListingPage />} />
                    <Route path="/edit-listing/:id" element={<CreateListingPage />} />
                    <Route path="/shop/:dealerSlug" element={<ShopPage />} />
                    <Route path="/shop/:dealerSlug/article/:articleId" element={<ShopArticlePage />} />
                    <Route path="/shop-checkout/success" element={<ShopCheckoutSuccessPage />} />
                    <Route path="/shop-checkout/cancelled" element={<ShopCheckoutCancelledPage />} />
                    <Route path="/my-orders" element={<MyOrdersPage />} />
                    <Route path="/dealer/:id" element={<DealerProfilePage />} />
                    <Route path="/dealers" element={<DealersPage />} />
                    <Route path="/leaderboard" element={<LeaderboardPage />} />
                    <Route path="/auctions" element={<AuctionsPage />} />
                    <Route path="/payment/success" element={<PaymentSuccessPage />} />
                    <Route path="/admin" element={<AdminDashboardPage />} />
                    <Route path="/admin/system/categories" element={<AdminSystemCategoriesPage />} />
                    <Route path="/admin/contact-leads" element={<AdminContactLeadsPage />} />
                    <Route path="/admin/hilfe-uebersicht" element={<HelpOverviewPage />} />
                    <Route path="/hilfe" element={<HelpOverviewPage />} />
                    <Route path="/admin/top-listing" element={<AdminTopListingPage />} />
                    <Route path="/auth/callback" element={<AuthCallbackPage />} />
                    <Route path="/admin/listing/:id/log" element={<AdminListingLogPage />} />
                    <Route path="/admin/dealers" element={<AdminDealersPage />} />
                    <Route path="/admin/impersonation" element={<AdminImpersonationPage />} />
                    <Route path="/admin/tickets" element={<AdminTicketsPage />} />
                    <Route path="/admin/assistant-stats" element={<AdminAssistantStatsPage />} />
                    <Route path="/admin/dealers/:id/audit" element={<AdminDealerAuditPage />} />
                    <Route path="/admin/compliance" element={<AdminCompliancePage />} />
                    <Route path="/admin/doku" element={<AdminDokuPage />} />
                    <Route path="/admin/gobd" element={<AdminGoBDPage />} />
                    <Route path="/admin/compliance/history" element={<AdminComplianceHistoryPage />} />
                    <Route path="/admin/compliance/spam" element={<AdminSpamFlagsPage />} />
                    <Route path="/ki-features" element={<KiFeaturesPage />} />
                    <Route path="/was-kann-twowheels" element={<WasKannTwoWheelsPage />} />
                    <Route path="/funktionen" element={<WasKannTwoWheelsPage />} />
                    <Route path="/vermittler" element={<VermittlerDashboardPage />} />
                    <Route path="/admin/vermittlers" element={<AdminVermittlersPage />} />
                    <Route path="/admin/konten" element={<AdminAccountToolsPage />} />
                    <Route path="/admin/newsletter" element={<AdminNewsletterPage />} />
                    <Route path="/admin/vermittler-referrals" element={<AdminVermittlerReferralsPage />} />
                    <Route path="/admin/sms/history" element={<AdminSmsHistoryPage />} />
                    <Route path="/admin/portal-consent" element={<AdminPortalConsentLogPage />} />
                    <Route path="/admin/tariffs" element={<AdminTariffsPage />} />
                    <Route path="/admin/usage-billing" element={<AdminUsageBillingPage />} />
                    <Route path="/admin/psttg" element={<AdminPstgReportPage />} />
                    <Route path="/admin/listing-reports" element={<AdminListingReportsPage />} />
                    <Route path="/admin/reviews" element={<AdminReviewsPage />} />
                    <Route path="/admin/points" element={<AdminPointsPage />} />
                    <Route path="/admin/pawn" element={<AdminPawnPage />} />
                    <Route path="/register/service-provider" element={<ServiceProviderRegisterPage />} />
                    <Route path="/dienstleister/dashboard" element={<ServiceProviderDashboard />} />
                    <Route path="/admin/service-providers" element={<AdminServiceProvidersPage />} />
                    <Route path="/dealer/werbemittel" element={<DealerWerbemittelPage />} />
                    <Route path="/listing/:id/qrcard" element={<ListingQrCardPage />} />
                    <Route path="/admin/wallet" element={<AdminWalletPage />} />
                    <Route path="/admin/leads" element={<AdminLeadsPage />} />
                    <Route path="/team" element={<TeamShowcasePage />} />
                    <Route path="/v/:code" element={<ReferralLandingPage />} />
                    <Route path="/buy-request/:id" element={<DealerBuyRequestDetailPage />} />
                    <Route path="/legal/buy-request-terms" element={<BuyRequestTermsPage />} />
                    <Route path="/legal/agb" element={<AgbPage />} />
                    <Route path="/legal/datenschutz" element={<DatenschutzPage />} />
                    <Route path="/legal/impressum" element={<ImpressumPage />} />
                    {/* Feb-2026 (User-Wunsch 25 May, Update v5): Short-Aliases für Footer-Links */}
                    <Route path="/agb" element={<AgbPage />} />
                    <Route path="/datenschutz" element={<DatenschutzPage />} />
                    <Route path="/impressum" element={<ImpressumPage />} />
                    <Route path="/konto/meine-daten" element={<MeineDatenPage />} />
                    <Route path="/legal/avv" element={<AvvPage />} />
                    <Route path="/legal/haendler-nutzungsbedingungen" element={<DealerTermsPage />} />
                    <Route path="/widerruf" element={<WiderrufPage />} />
                    <Route path="/barrierefreiheit" element={<BarrierefreiheitPage />} />
                    <Route path="/legal/compliance" element={<CompliancePage />} />
                    <Route path="/ankauf" element={<BuyRequestPage />} />
                    <Route path="/ankauf/status/:id" element={<BuyRequestStatusPage />} />
                    <Route path="/compare" element={<ComparePage />} />
                    <Route path="/werkstatt/:dealerId/termin" element={<WorkshopBookingPage />} />
                </Routes>
                <CompareDrawer />
            </main>
            {showChrome && <Footer />}
            {showChrome && <MobileNotifyPrompt />}
            {showChrome && <MobileBottomNav />}
            {user && <CommandPalette user={user} />}
            {user && <CommandPaletteFAB />}
            {user && <OnboardingTour user={user} />}
            <TaskTitleBadge enabled={!!user && (user.role === "b2b" || user.role === "zweiradverkaeufer")} />
            {user && <TaskPushPrompt />}
            <ChatbotWidget />
            <Toaster position="top-center" richColors />
            <PdfPreviewHost />
            <ConfirmDialogHost />
            <CookieConsent />
        </div>
    );
}

export default App;
